/**
 * AI Classification Service v3
 * Frontend service for the 4-phase AI classification pipeline.
 * Based on specification from AI-Classification-Framework-v3.md
 */
import api from './api';

/**
 * Classify a part using the v3 AI pipeline.
 * Returns progressive phase updates for UI display.
 * 
 * @param {Object} partData - Part data to classify
 * @param {string} partData.manufacturer - Manufacturer name
 * @param {string} partData.mpn - Manufacturer part number
 * @param {string} partData.description - Part description
 * @param {string} [partData.uom='EA'] - Unit of measure
 * @returns {Promise<Object>} Classification result with phase details
 */
export async function classifyPartV3(partData) {
  const response = await api.post('/api/v1/ai/v3/classify-part', partData);
  return response.data;
}

/**
 * Classify a part using streaming SSE for real-time phase updates.
 * 
 * @param {Object} partData - Part data to classify
 * @param {Function} onPhaseStart - Callback when a phase starts
 * @param {Function} onPhaseComplete - Callback when a phase completes
 * @param {Function} onComplete - Callback when classification completes
 * @param {Function} onError - Callback on error
 * @returns {Function} Cleanup function to abort the stream
 */
export function classifyPartV3Stream(partData, { onPhaseStart, onPhaseComplete, onComplete, onError }) {
  const controller = new AbortController();
  const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
  
  const fetchStream = async () => {
    try {
      const response = await fetch(`${baseUrl}/api/v1/ai/v3/classify-part-stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(partData),
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        
        // Process complete SSE events
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const event = JSON.parse(line.slice(6));
              
              switch (event.type) {
                case 'phase_start':
                  if (onPhaseStart) onPhaseStart(event);
                  break;
                case 'phase_complete':
                  if (onPhaseComplete) onPhaseComplete(event);
                  break;
                case 'classification_complete':
                  if (onComplete) onComplete(event);
                  break;
                case 'error':
                  if (onError) onError(new Error(event.error));
                  break;
                default:
                  console.log('[AI Stream] Unknown event:', event);
              }
            } catch (parseError) {
              console.error('[AI Stream] Parse error:', parseError);
            }
          }
        }
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('[AI Stream] Error:', error);
        if (onError) onError(error);
      }
    }
  };

  fetchStream();
  
  // Return cleanup function
  return () => controller.abort();
}

/**
 * Override AI classification with user selection.
 * 
 * @param {Object} overrideData
 * @param {string} overrideData.classificationLogId - Original classification log ID
 * @param {string} overrideData.newMainGroupCode - User-selected main group
 * @param {string} [overrideData.newSubGroupId] - Existing subgroup ID (if selecting existing)
 * @param {string} [overrideData.newSubGroupName] - New subgroup name (if creating new)
 * @param {string} overrideData.reason - Reason for override
 * @returns {Promise<Object>} Override result
 */
export async function overrideClassification(overrideData) {
  const response = await api.post('/api/v1/ai/v3/override', overrideData);
  return response.data;
}

/**
 * Get classification logs with pagination and filters.
 * 
 * @param {Object} [params] - Query parameters
 * @param {number} [params.page=1] - Page number
 * @param {number} [params.pageSize=20] - Page size
 * @param {string} [params.manufacturer] - Filter by manufacturer
 * @param {boolean} [params.isNewSubgroup] - Filter by new subgroup creation
 * @param {string} [params.userAction] - Filter by user action (accepted/overridden)
 * @returns {Promise<Object>} Classification logs
 */
export async function getClassificationLogs(params = {}) {
  const response = await api.get('/api/v1/ai/v3/logs', { params });
  return response.data;
}

/**
 * Get detailed classification log by ID.
 * 
 * @param {string} logId - Classification log ID
 * @returns {Promise<Object>} Detailed classification log
 */
export async function getClassificationLogDetail(logId) {
  const response = await api.get(`/api/v1/ai/v3/logs/${logId}`);
  return response.data;
}

/**
 * Get subgroup metadata.
 * 
 * @param {string} subgroupCode - Subgroup code
 * @returns {Promise<Object>} Subgroup metadata
 */
export async function getSubgroupMetadata(subgroupCode) {
  const response = await api.get(`/api/v1/ai/v3/subgroup/${subgroupCode}/metadata`);
  return response.data;
}

/**
 * Update subgroup metadata (additive only).
 * 
 * @param {string} subgroupCode - Subgroup code
 * @param {Object} updates - Updates to apply
 * @returns {Promise<Object>} Update result
 */
export async function updateSubgroupMetadata(subgroupCode, updates) {
  const response = await api.put(`/api/v1/ai/v3/subgroup/${subgroupCode}/metadata`, updates);
  return response.data;
}

/**
 * Get pending insights summary for all subgroups.
 * 
 * @returns {Promise<Object>} Pending insights summary
 */
export async function getPendingInsights() {
  const response = await api.get('/api/v1/ai/v3/pending-insights');
  return response.data;
}

/**
 * Trigger manual consolidation for a subgroup.
 * 
 * @param {string} subGroupId - Subgroup ID to consolidate
 * @returns {Promise<Object>} Consolidation result
 */
export async function triggerConsolidation(subGroupId) {
  const response = await api.post('/api/v1/ai/v3/consolidate', { subGroupId });
  return response.data;
}

/**
 * Check AI service v3 health.
 * 
 * @returns {Promise<Object>} Health status
 */
export async function checkHealthV3() {
  const response = await api.get('/api/v1/ai/v3/health');
  return response.data;
}

/**
 * Re-classify an existing part using AI.
 * 
 * @param {string} hpnCompact - Part HPN (compact format)
 * @param {boolean} [saveTopart=true] - Whether to save classification data to part document
 * @returns {Promise<Object>} Reclassification result
 */
export async function reclassifyPart(hpnCompact, saveToPart = true) {
  const response = await api.post('/api/v1/ai/v3/reclassify-part', {
    hpn_compact: hpnCompact,
    save_to_part: saveToPart
  });
  return response.data;
}

export default {
  classifyPartV3,
  classifyPartV3Stream,
  overrideClassification,
  getClassificationLogs,
  getClassificationLogDetail,
  getSubgroupMetadata,
  updateSubgroupMetadata,
  getPendingInsights,
  triggerConsolidation,
  checkHealthV3,
  reclassifyPart,
};
